/**
 * Production-grade React error boundary.
 *
 * - Initializes Sentry on the client when REACT_APP_SENTRY_DSN is set.
 * - Wraps the app in <Sentry.ErrorBoundary> so any uncaught render error
 *   is reported AND replaced with a friendly fallback (no white screen).
 * - Fallback offers a "Try again" reset + a "Go home" escape hatch.
 *
 * Backend Sentry is already configured separately in
 * /app/backend/middleware/production.py — this is the frontend half.
 */
import React from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;

if (SENTRY_DSN && !window.__SENTRY_INITIALIZED__) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENV || process.env.NODE_ENV,
    release: process.env.REACT_APP_VERSION || 'quotesnapp@dev',
    tracesSampleRate: 0.1,
    // Replay is heavy; sample sparingly. Errors always replay.
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    // Don't spam Sentry with network noise the user can't act on.
    ignoreErrors: [
      'Network Error',
      'NetworkError',
      'Failed to fetch',
      'Load failed',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.',
      'Non-Error promise rejection captured',
    ],
  });
  window.__SENTRY_INITIALIZED__ = true;
}

const FallbackUI = ({ error, resetError, eventId }) => (
  <div
    role="alert"
    data-testid="error-boundary-fallback"
    className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-6"
  >
    <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-red-100 p-8 text-center">
      <div className="mx-auto w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-4">
        <AlertTriangle className="w-7 h-7 text-red-600" />
      </div>
      <h1 className="text-2xl font-bold text-slate-900 mb-2">Something went wrong</h1>
      <p className="text-slate-600 mb-6">
        QuoteSnapp hit an unexpected error and couldn't finish loading this page.
        Our team has been notified.
      </p>

      {process.env.NODE_ENV !== 'production' && error && (
        <pre
          className="text-xs text-left bg-slate-50 border border-slate-200 rounded-lg p-3 mb-6 overflow-auto max-h-40 text-red-700"
          data-testid="error-boundary-dev-details"
        >
          {String(error?.message || error)}
        </pre>
      )}

      {eventId && (
        <p className="text-xs text-slate-500 mb-4" data-testid="error-boundary-event-id">
          Reference: {eventId}
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          type="button"
          onClick={resetError}
          className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-3 rounded-lg transition-colors"
          data-testid="error-boundary-retry-button"
        >
          <RefreshCw className="w-4 h-4" />
          Try again
        </button>
        <a
          href="/"
          className="inline-flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold px-5 py-3 rounded-lg transition-colors"
          data-testid="error-boundary-home-button"
        >
          <Home className="w-4 h-4" />
          Go home
        </a>
      </div>
    </div>
  </div>
);

const ErrorBoundary = ({ children }) => (
  <Sentry.ErrorBoundary
    fallback={(props) => <FallbackUI {...props} />}
    showDialog={false}
  >
    {children}
  </Sentry.ErrorBoundary>
);

export default ErrorBoundary;
